import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, CardHeader, ErrorSummary, Input, LoadingSpinner, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { quoteDeskService, type DeskDetail } from '@/services/insurerQuoteDesk';
import { useAuthStore } from '@/store';
import { formatCurrency, formatDateTime } from '@/utils';
import { QUOTE_DESK_QUERY_KEY, TatBadge } from './QuoteDeskPage';

/**
 * Insurer portal > Quote Desk > one request (#399).
 *
 * Left: the request, limited to the underwriting allow-list the server sends.
 * Right: pricing and the decision. Below: the broker conversation, the
 * internal notes (this insurer's staff only) and the history.
 *
 * Buttons mirror the server: approve/decline need approval authority, and a
 * figure beyond the adjustment limit offers Escalate instead. Every refusal
 * still comes from the API and is shown as its own message.
 */

const ACTIVE = ['open', 'in_review', 'info_requested'];

function money(value: string | null | undefined) {
  return value == null || value === '' ? '—' : formatCurrency(Number(value));
}

/** "+4.2% vs reference (limit 10%)", or null when there is no reference. */
// eslint-disable-next-line react-refresh/only-export-components
export function adjustmentIndicator(reference: string | null, premium: string, limitPct: string) {
  const ref = Number(reference);
  const value = Number(premium);
  if (!reference || !ref || !premium || Number.isNaN(value)) return null;
  const pct = ((value - ref) / ref) * 100;
  const limit = Number(limitPct);
  return {
    pct,
    overLimit: Math.abs(pct) > limit + 1e-9,
    label: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs reference (limit ${limit}%)`,
  };
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-900 dark:text-white">{value === '' || value == null ? '—' : value}</dd>
    </div>
  );
}

function TextModal({
  title,
  label,
  onClose,
  onSubmit,
  busy,
  children,
  minLength = 1,
}: {
  title: string;
  label: string;
  onClose: () => void;
  onSubmit: (text: string) => void;
  busy: boolean;
  children?: React.ReactNode;
  minLength?: number;
}) {
  const [text, setText] = useState('');
  return (
    <Modal isOpen onClose={onClose} title={title} size="md">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim().length >= minLength) onSubmit(text.trim());
        }}
      >
        {children}
        <label htmlFor="desk-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        <textarea
          id="desk-text"
          rows={4}
          required
          minLength={minLength}
          className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={busy} disabled={text.trim().length < minLength}>
            Send
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function RequestPanel({ detail }: { detail: DeskDetail }) {
  const r = detail.request;
  const vehicle = r.vehicle ? `${r.vehicle.make} ${r.vehicle.model} ${r.vehicle.variant}`.trim() : '';
  return (
    <Card>
      <CardHeader title="Request" subtitle={`${detail.broker.name} · ${detail.requester_type === 'posp' ? 'POSP' : 'Broker'}`} />
      {detail.request_updated_since_submit && (
        <p className="mb-3 rounded bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          The broker updated this request after sending it.
        </p>
      )}
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Line of business" value={detail.insurance_type.name} />
        <Field label="Insured" value={String(r.proposer_name ?? '')} />
        <Field label="Business type" value={String(r.business_type ?? '')} />
        <Field label="Cover" value={String(r.cover_type ?? '')} />
        {vehicle && <Field label="Vehicle" value={vehicle} />}
        {r.vehicle_idv != null && <Field label="IDV" value={money(r.vehicle_idv as string)} />}
        {r.sum_insured != null && <Field label="Sum insured" value={money(r.sum_insured as string)} />}
        {r.manufacturing_year != null && <Field label="Manufacturing year" value={String(r.manufacturing_year)} />}
        {r.rto_code ? <Field label="RTO" value={String(r.rto_code)} /> : null}
        <Field label="Previous insurer" value={String(r.previous_insurer_name ?? '')} />
        <Field label="Previous NCB" value={r.previous_ncb != null ? `${r.previous_ncb}%` : ''} />
        <Field label="Claim last year" value={r.has_claim_in_previous ? 'Yes' : 'No'} />
        <Field label="City" value={String(r.proposer_city ?? '')} />
        <Field label="Origin" value={detail.origin} />
      </dl>
      {r.members.length > 0 && (
        <ul className="mt-3 text-sm text-gray-700 dark:text-gray-300">
          {r.members.map((m, i) => (
            <li key={i}>
              {m.relation || 'Member'}
              {m.age != null ? `, ${m.age}` : ''}
              {m.pre_existing_conditions ? ' · pre-existing condition' : ''}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4">
        <p className="text-xs text-gray-500 dark:text-gray-400">Broker note</p>
        <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">{detail.broker_note}</p>
      </div>
    </Card>
  );
}

export function QuoteDeskDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const key = [...QUOTE_DESK_QUERY_KEY, 'detail', id];
  const query = useQuery({ queryKey: key, queryFn: () => quoteDeskService.get(id), enabled: !!id });
  const [premium, setPremium] = useState<string | null>(null);
  const [product, setProduct] = useState<string | null>(null);
  const [validUntil, setValidUntil] = useState('');
  const [modal, setModal] = useState<null | 'approve' | 'decline' | 'info' | 'escalate'>(null);
  const [declineCode, setDeclineCode] = useState('risk_not_acceptable');
  const [note, setNote] = useState('');
  const [tab, setTab] = useState<'conversation' | 'notes' | 'history'>('conversation');
  const [errors, setErrors] = useState<string[]>([]);

  const onDone = (data: DeskDetail, message: string) => {
    queryClient.setQueryData(key, data);
    queryClient.invalidateQueries({ queryKey: QUOTE_DESK_QUERY_KEY });
    setModal(null);
    setErrors([]);
    toast.success(message);
  };
  const act = useMutation({
    mutationFn: ({ name, body }: { name: string; body?: Record<string, unknown>; message: string }) =>
      quoteDeskService.action(id, name, body),
    onSuccess: (data, vars) => onDone(data, vars.message),
    onError: (err) => setErrors([getErrorMessage(err)]),
  });
  const addNote = useMutation({
    mutationFn: () => quoteDeskService.addNote(id, note),
    onSuccess: () => {
      setNote('');
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) => setErrors([getErrorMessage(err)]),
  });
  const removeNote = useMutation({
    mutationFn: (noteId: string) => quoteDeskService.deleteNote(id, noteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (err) => setErrors([getErrorMessage(err)]),
  });

  if (query.isLoading) return <LoadingSpinner />;
  if (query.isError || !query.data) {
    return <p className="text-sm text-red-600 dark:text-red-400">{getErrorMessage(query.error)}</p>;
  }
  const d = query.data;
  const auth = d.authority;
  const active = ACTIVE.includes(d.status);
  const decidable = d.status === 'open' || d.status === 'in_review';
  const revisable = d.status === 'approved' && d.quote && !['selected', 'converted'].includes(d.quote.status);
  const figure = premium ?? d.proposed_premium ?? d.reference_premium ?? '';
  const chosenProduct = product ?? d.proposed_product?.id ?? d.products[0]?.id ?? '';
  const indicator = adjustmentIndicator(d.reference_premium, figure, auth.adjust_limit_pct);
  const overLimit = Boolean(indicator?.overLimit) && !auth.is_admin;
  const overCap =
    !auth.is_admin && auth.max_approval_amount != null && figure !== '' && Number(figure) > Number(auth.max_approval_amount);
  const canApproveNow = auth.can_approve && !overLimit && !overCap && (decidable || revisable);
  const pricingBody = () => ({
    total_premium: figure,
    product: chosenProduct || undefined,
    // The date means the END of that day in the underwriter's own time zone;
    // `new Date('2026-11-05')` is midnight UTC, which cut the last day off.
    valid_until: validUntil ? new Date(`${validUntil}T23:59:59`).toISOString() : undefined,
  });

  return (
    <div>
      <PageHeader
        title={d.request_number}
        subtitle={d.insurance_type.name}
        breadcrumbs={[{ label: 'Quote Desk', href: '/insurance/quote-desk' }, { label: d.request_number }]}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={d.status === 'approved' ? 'success' : d.status === 'declined' ? 'error' : 'info'}>
              {d.status_display}
            </Badge>
            <TatBadge row={d} />
            {auth.can_edit && active && !d.assigned_to && (
              <Button size="sm" variant="outline" onClick={() => act.mutate({ name: 'claim', message: 'Assigned to you' })}>
                Claim
              </Button>
            )}
          </div>
        }
      />
      <p className="-mt-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
        Assigned to {d.assigned_to?.name ?? 'nobody yet'}
        {d.escalated_at ? ' · escalated to your company admin' : ''}
      </p>
      <ErrorSummary messages={errors} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <RequestPanel detail={d} />

        <Card>
          <CardHeader title="Pricing and decision" />
          <dl className="mb-4 grid grid-cols-2 gap-3">
            <Field label="Reference premium" value={d.reference_premium ? money(d.reference_premium) : 'No reference premium'} />
            <Field label="Broker target" value={money(d.target_premium)} />
          </dl>
          {d.status === 'approved' || d.status === 'declined' ? (
            <div className="mb-4 rounded bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
              <p className="font-medium text-gray-900 dark:text-white">
                {d.status === 'approved' ? `Approved at ${money(d.quote?.total_premium)}` : 'Declined'}
              </p>
              {d.decision_reason && <p className="text-gray-700 dark:text-gray-300">{d.decision_reason}</p>}
            </div>
          ) : null}
          {auth.can_edit && (decidable || revisable) ? (
            <div className="space-y-3">
              <div>
                <label htmlFor="desk-product" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Product
                </label>
                <select
                  id="desk-product"
                  className="mt-1 w-full appearance-none select-chevron rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  value={chosenProduct}
                  onChange={(e) => setProduct(e.target.value)}
                >
                  {d.products.length === 0 && <option value="">No product of yours matches this line</option>}
                  {d.products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                id="desk-premium"
                label="Total premium"
                type="number"
                min={0}
                step="0.01"
                value={figure}
                onChange={(e) => setPremium(e.target.value)}
              />
              {indicator && (
                <p
                  data-testid="adjustment-indicator"
                  className={`text-sm ${indicator.overLimit ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  {indicator.label}
                </p>
              )}
              {overCap && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  Above your approval amount of {money(auth.max_approval_amount)}.
                </p>
              )}
              <Input
                id="desk-valid-until"
                label={`Valid until (default ${auth.validity_days} days)`}
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
              <div className="flex flex-wrap gap-2">
                {decidable && (
                  <Button
                    variant="outline"
                    disabled={!figure}
                    isLoading={act.isPending && act.variables?.name === 'adjust'}
                    onClick={() => act.mutate({ name: 'adjust', body: pricingBody(), message: 'Draft saved' })}
                  >
                    Save draft
                  </Button>
                )}
                <Button
                  disabled={!canApproveNow || !figure || !chosenProduct}
                  title={auth.can_approve ? undefined : 'Needs approval authority'}
                  onClick={() => setModal('approve')}
                >
                  {revisable ? 'Revise approval' : 'Approve'}
                </Button>
                {decidable && (
                  <Button
                    variant="danger"
                    disabled={!auth.can_approve}
                    title={auth.can_approve ? undefined : 'Needs approval authority'}
                    onClick={() => setModal('decline')}
                  >
                    Decline
                  </Button>
                )}
                {decidable && (
                  <Button variant="outline" onClick={() => setModal('info')}>
                    Ask broker for info
                  </Button>
                )}
                {decidable && (overLimit || overCap || !auth.can_approve) && (
                  <Button variant="outline" onClick={() => setModal('escalate')}>
                    Escalate
                  </Button>
                )}
              </div>
            </div>
          ) : (
            d.status === 'info_requested' && (
              <p className="text-sm text-gray-600 dark:text-gray-400">Clock paused: waiting for the broker&apos;s reply.</p>
            )
          )}
        </Card>
      </div>

      <Card className="mt-6">
        <div className="mb-4 flex gap-4 border-b border-gray-200 dark:border-gray-700" role="tablist">
          {(
            [
              ['conversation', 'Conversation'],
              ['notes', 'Internal notes'],
              ['history', 'History'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              role="tab"
              type="button"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={`-mb-px border-b-2 pb-2 text-sm ${
                tab === value ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500'
              }`}
            >
              {value === 'notes' && <LockClosedIcon className="mr-1 inline h-4 w-4" />}
              {label}
            </button>
          ))}
        </div>
        {tab === 'conversation' && (
          <ul className="space-y-3">
            {d.messages.map((m) => (
              <li key={m.id} className="text-sm">
                <span className="font-medium text-gray-900 dark:text-white">{m.author || 'System'}</span>{' '}
                <span className="text-xs text-gray-500">{formatDateTime(m.created_at)}</span>
                <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{m.body}</p>
              </li>
            ))}
          </ul>
        )}
        {tab === 'notes' && (
          <div>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Visible to your company only.</p>
            <ul className="space-y-3">
              {d.notes.map((n) => (
                <li key={n.id} className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{n.author?.name ?? ''}</span>{' '}
                  <span className="text-xs text-gray-500">{formatDateTime(n.created_at)}</span>
                  {n.can_delete && (
                    <button
                      type="button"
                      className="ml-2 text-xs text-red-600"
                      onClick={() => removeNote.mutate(n.id)}
                    >
                      Remove
                    </button>
                  )}
                  <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{n.body}</p>
                </li>
              ))}
            </ul>
            {auth.can_edit && (
              <form
                className="mt-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (note.trim()) addNote.mutate();
                }}
              >
                <label htmlFor="desk-note" className="sr-only">
                  Internal note
                </label>
                <textarea
                  id="desk-note"
                  rows={3}
                  maxLength={4000}
                  placeholder="Add an internal note"
                  className="w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <Button type="submit" size="sm" className="mt-2" isLoading={addNote.isPending} disabled={!note.trim()}>
                  Add note
                </Button>
              </form>
            )}
          </div>
        )}
        {tab === 'history' && (
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            {d.events.map((e, i) => (
              <li key={i}>
                {formatDateTime(e.created_at)}: {e.event.replace(/_/g, ' ')}
                {e.actor ? ` by ${e.actor.name}` : e.actor_label ? ` by ${e.actor_label}` : ''}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {modal === 'approve' && (
        <Modal isOpen onClose={() => setModal(null)} title="Approve this premium" size="sm">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {money(figure)} on {d.products.find((p) => p.id === chosenProduct)?.name ?? 'the selected product'}, valid{' '}
            {validUntil ? `until ${validUntil}` : `for ${auth.validity_days} days`}. The broker sees it as an option on
            their quote.
          </p>
          <ModalFooter>
            <Button variant="outline" onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button
              isLoading={act.isPending}
              onClick={() => act.mutate({ name: 'approve', body: pricingBody(), message: 'Pricing approved' })}
            >
              Approve
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {modal === 'decline' && (
        <TextModal
          title="Decline this request"
          label="Reason (the broker sees it)"
          minLength={5}
          busy={act.isPending}
          onClose={() => setModal(null)}
          onSubmit={(reason) =>
            act.mutate({ name: 'decline', body: { decision_code: declineCode, reason }, message: 'Request declined' })
          }
        >
          <label htmlFor="desk-decline-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason code
          </label>
          <select
            id="desk-decline-code"
            className="mb-3 mt-1 w-full appearance-none select-chevron rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            value={declineCode}
            onChange={(e) => setDeclineCode(e.target.value)}
          >
            {d.decline_codes.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </TextModal>
      )}
      {modal === 'info' && (
        <TextModal
          title="Ask the broker for information"
          label="Your question"
          busy={act.isPending}
          onClose={() => setModal(null)}
          onSubmit={(message) =>
            act.mutate({ name: 'request-info', body: { message }, message: 'Question sent. Clock paused.' })
          }
        />
      )}
      {modal === 'escalate' && (
        <TextModal
          title="Escalate to your company admin"
          label="Note for your admin"
          busy={act.isPending}
          onClose={() => setModal(null)}
          onSubmit={(text) =>
            act.mutate({
              name: 'escalate',
              body: { note: text, total_premium: figure || undefined },
              message: 'Escalated to your admin',
            })
          }
        />
      )}
      {user?.role === 'insurer_admin' && d.escalation_note && (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Escalation note: {d.escalation_note}</p>
      )}
    </div>
  );
}

export default QuoteDeskDetailPage;
